import { Box } from "@mui/material";
import { FormikProps } from "formik";
import { useMemo } from "react";
import Flex from "../../../../../../../../../../components/Flex";
import MaskMultiSelect from "../../../../../../../../../../components/MaskMultiSelect";
import { ScrollArea } from "../../../../../../../../../../components/ScrollBar";
import Text from "../../../../../../../../../../components/Text";
import DeleteIcon from "../../../../../../../../../../components/app/DeleteIcon";
import StyledFormModal from "../../../../../../../../../../components/globalModal/StyledModal";
import StyledButtonV2 from "../../../../../../../../../../components/app/StyledButtonV2";
import { useDebounce } from "../../../../../../../../../../hooks/use-debounce";
import { DisclosureHost } from "../../../../../../../../../../hooks/use-disclosure";
import useGetCataLogCode from "../../../../../../../../../../queries/catalog/use-get-catalog-code";
import { Form2Payload } from "@/pages/app/provider/scheduling/appointment/encounter/encounter-constant";
import useFormDraft from "@/hooks/use-form-draft";
import {
  DEFAULT_BG_COLOR,
  DEFAULT_FONTSIZE,
  DEF_PAGINATION,
} from "../../../../../../../../../../utils/utils";
/** A code as stored on the note: `{ value, code }`. */
type Code = NonNullable<Form2Payload["procedureCodes"]>[number];

const PROCEDURE_CODES_FIELD = "procedureCodes";

interface AddCptCodesProps {
  formik: FormikProps<Form2Payload>;
  disclosureHost: DisclosureHost;
}

// Kept here for the note sections that render the selected list outside this modal.

export const renderSelectedCptCodes = (
  selectedCptCodes: Code[],
  handleDeleteCptCode: (code: Code) => void,
  height?: string
) => {
  if (!selectedCptCodes || selectedCptCodes.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        mt: 2,
      }}
    >
      <Text fontWeight="medium" fontSize="12px" color="#373D41" mb={1}>
        Selected CPT Codes:
      </Text>
      <ScrollArea maxHeight={height ?? "35vh"} height={"100%"}>
        <Flex flexDirection="column" gap={1}>
          {selectedCptCodes.map((code, index) => {
            // Parse the code to extract  CPT code and description
            const codeString =
              typeof code === "string" ? code : code?.code || "";
            const codeParts = codeString.split(" - ");
            const cptCode = codeParts[0];
            const description = codeParts.slice(1).join(" - ");

            return (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: DEFAULT_BG_COLOR,
                  borderRadius: "4px",
                  padding: "6px 8px",
                  gap: "12px",
                }}
              >
                <Flex alignItems="center" gap={0.5}>
                  <Text fontSize={DEFAULT_FONTSIZE} fontWeight="400">
                    {cptCode}
                  </Text>
                  <Text fontSize={DEFAULT_FONTSIZE} fontWeight="400">
                    - {description}
                  </Text>
                </Flex>
                <DeleteIcon onClick={() => handleDeleteCptCode(code)} />
              </Box>
            );
          })}
        </Flex>
      </ScrollArea>
    </Box>
  );
};

export const AddCptCodes = ({ formik, disclosureHost }: AddCptCodesProps) => {
  const [searchCptKey, setSearchCptKey] = useDebounce("");
  const { data: cptCodes } = useGetCataLogCode({
    pagination: DEF_PAGINATION,
    searchKey: searchCptKey,
    type: "CPT",
    active: true,
    archive: false,
    enabled: disclosureHost.opened,
  });
  // The picker stays mounted between openings, so the selection is buffered in a
  // draft that re-snapshots the note every time it opens — a code deleted from
  // the note would otherwise still show as selected in here.
  const draft = useFormDraft(
    formik,
    [PROCEDURE_CODES_FIELD],
    disclosureHost.opened
  );
  const selectedCptCodes = useMemo(
    () => draft.values?.procedureCodes ?? [],
    [draft.values?.procedureCodes]
  );
  const setSelectedCptCodes = (codes: Code[]) =>
    draft.setFieldValue(PROCEDURE_CODES_FIELD, codes);
  const fetchedCodes: Code[] = useMemo(
    () =>
      cptCodes?.data?.content?.map((it) => ({
        value: it?.uuid ?? "",
        code: `${it?.code} - ${it?.description || ""}`,
      })) ?? [],
    [cptCodes]
  );

  // Only the codes returned for the current search are offered. Retaining the
  // pages fetched earlier would keep every previous result in the list and push
  // the freshly matched code to the bottom, where it reads as "not returned".
  // Selected codes are still prepended so the dropdown - which resolves its
  // labels from `options` - can render them when the current page omits them.
  const transFormedCptCodes = useMemo(
    () => [
      ...selectedCptCodes.filter(
        (selected) =>
          !fetchedCodes.some((option) => option.value === selected?.value)
      ),
      ...fetchedCodes,
    ],
    [fetchedCodes, selectedCptCodes]
  );

  const handleDeleteCptCode = (codeToDelete: Code) =>
    setSelectedCptCodes(
      selectedCptCodes.filter((code) => code?.value !== codeToDelete?.value)
    );

  return (
    <StyledFormModal
      open={disclosureHost.opened}
      onClose={() => disclosureHost.close()}
      dialogTitle="Add CPT Codes"
      dialogAction={
        <>
          <StyledButtonV2
            label="Close"
            variant="outlined"
            onClick={() => {
              disclosureHost.close();
            }}
            sx={{ width: "100px" }}
          />
          <StyledButtonV2
            label="Add"
            variant="outlined"
            onClick={() => {
              draft.save();
              disclosureHost.close();
            }}
            sx={{
              width: "110px",
              backgroundColor: "#CCECFF80",
              color: "#004186",
            }}
          />
        </>
      }
      sx={{
        "& .MuiDialog-paper": {
          width: "40vw",
          height: "65vh",
        },
      }}
    >
      <Box
        sx={{
          mt: 1,
          width: "100%",
          pt: 2,
        }}
      >
        <MaskMultiSelect
          placeholder="Search & Select CPT Codes"
          value={selectedCptCodes}
          onSearch={setSearchCptKey}
          options={transFormedCptCodes}
          onChange={(v) => setSelectedCptCodes(v ?? [])}
        />
        {renderSelectedCptCodes(selectedCptCodes, handleDeleteCptCode, "35vh")}
      </Box>
    </StyledFormModal>
  );
};
